/**
 * Notification schemas and constants for the in-app notification system.
 *
 * Traceability: reservation-reminder-in-app-notifications / spec.md
 *
 * Design notes:
 * - recipientUserId intentionally omitted — implicit from auth scope
 * - outboxEventIds intentionally omitted — internal implementation detail
 * - payload is loosely typed (z.record) — actual shape is a worker concern per strategy
 */
import { z } from 'zod';
import { createPaginatedRequestSchema, createPaginatedResponseSchema } from './pagination.js';

// ─── Queue name constants (consumed by worker boundary tasks 3.1–3.3) ────────

export const NOTIFICATIONS_DISPATCH_QUEUE_NAME = 'notifications-dispatch' as const;
export const NOTIFICATIONS_PURGE_QUEUE_NAME = 'notifications-purge' as const;

// ─── Payload allowlist ───────────────────────────────────────────────────────
// Only these fields are stored in the DB and returned through the API.
// Never expose holderUserId or other user-scoped reservation fields.
// This constant and helper are the single source of truth — updated in one place.
export const NOTIFICATION_SAFE_PAYLOAD_KEYS = ['offerId', 'mediaUnitId', 'ttlExpiresAt'] as const;
export type NotificationSafePayloadKey = (typeof NOTIFICATION_SAFE_PAYLOAD_KEYS)[number];

/**
 * Projects only the allowlisted keys from a raw event payload.
 * Used by both the worker (at write time) and the API service (at read time).
 */
export function projectSafePayload(raw: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    NOTIFICATION_SAFE_PAYLOAD_KEYS.filter((k) => raw[k] !== undefined).map((k) => [k, raw[k]]),
  );
}

// ─── Core notification DTO ────────────────────────────────────────────────────

export const NotificationSchema = z.object({
  id: z.number().int().positive(),
  eventType: z.string(),
  groupKey: z.string().nullable(),
  payload: z.record(z.string(), z.unknown()),
  count: z.number().int().min(1),
  readAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  // recipientUserId intentionally omitted — implicit from auth scope
  // outboxEventIds intentionally omitted — internal implementation detail
});
export type Notification = z.infer<typeof NotificationSchema>;

// ─── List query & response ────────────────────────────────────────────────────

export const NotificationListQuerySchema = createPaginatedRequestSchema(50);
export type NotificationListQuery = z.infer<typeof NotificationListQuerySchema>;

export const NotificationListResponseSchema = createPaginatedResponseSchema(NotificationSchema);
export type NotificationListResponse = z.infer<typeof NotificationListResponseSchema>;

// ─── Unread count response ────────────────────────────────────────────────────

export const UnreadCountResponseSchema = z.object({
  unreadCount: z.number().int().min(0),
});
export type UnreadCountResponse = z.infer<typeof UnreadCountResponseSchema>;

// ─── Mark read response (204 No Content) ─────────────────────────────────────

export const MarkReadResponseSchema = z.void();
